import parse from 'html-react-parser';
import React from "react";
import { useNavigate } from "react-router-dom";
import { Edit2, Eye } from "lucide-react";
import { capitalize } from "../../../../components/tools/utils.jsx";
import { useAuthContext } from "../../../../components/providers/AuthProvider.jsx";
import ActionsDropdown from "./ActionsDropdown.jsx";
import TokensDropdown from "./components/TokensDropdown.jsx";
import { editorPathForModel } from '../../../../products/registry';
import { dateCaption } from './dates.js';
import ProductTypeBadge from '../../../../products/ProductTypeBadge.jsx';
import ARViewer from '../../../Customiz3r/AR/ARViewer.jsx';

function removeWidthAndHeight(svgString) {
    return svgString
        .replace(/width="[^"]*"/, '')
        .replace(/height="[^"]*"/, '');
}

function Modelo3dCard({ modelo, showInfo = true, className = '', onDelete, onDuplicated, sortBy }) {
    const navigate = useNavigate();
    const authContext = useAuthContext();

    return (
        <div className={`group relative h-56 rounded-lg shadow-md z-0 focus-within:z-10
                         transition-all duration-200
                         hover:shadow-xl hover:scale-[1.03] hover:-translate-y-0.5
                         cursor-pointer ${className}`}>

            {/* Token badge — always visible, above overlay */}
            <div className="absolute top-2 left-2 z-20">
                <TokensDropdown modelo={modelo} />
            </div>

            <div className="absolute top-2 right-2 z-20">
                <ProductTypeBadge modelo={modelo} compact />
            </div>

            {/* Acciones apiladas desde la posición del engranaje. */}
            <div className="absolute bottom-[21%] right-2 z-20 flex flex-col items-center gap-2">
                {modelo.id && (
                    <div className="flex flex-col gap-2 opacity-0 pointer-events-none translate-y-2
                                    group-hover:opacity-100 group-hover:pointer-events-auto group-hover:translate-y-0
                                    group-focus-within:opacity-100 group-focus-within:pointer-events-auto group-focus-within:translate-y-0
                                    transition-all duration-200">
                        <button
                            className="btn btn-sm btn-square btn-primary shadow-lg"
                            onClick={() => navigate(editorPathForModel(modelo))}
                            title="Editar"
                            aria-label={`Editar ${modelo.name}`}
                        >
                            <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                            className="btn btn-sm btn-square btn-secondary shadow-lg"
                            onClick={() => navigate(`/viewer/${modelo.id}/`)}
                            title="Ver en 3D"
                            aria-label={`Ver ${modelo.name} en 3D`}
                        >
                            <Eye className="w-4 h-4" />
                        </button>
                        {modelo.has_3d !== false && (
                            <ARViewer
                                modelId={modelo.id}
                                revision={modelo.glb_revision || modelo.viewer_revision || modelo.revision || 1}
                                compact
                                inline
                                direct
                            />
                        )}
                    </div>
                )}
                <ActionsDropdown
                    modelo={modelo}
                    navigate={navigate}
                    authContext={authContext}
                    onDelete={onDelete}
                    onDuplicated={onDuplicated}
                    keyringId={modelo.id}
                    name={modelo.name}
                    compact
                />
            </div>

            {/* Vista 2D; si existe render 3D, aparece al hacer hover. */}
            <div className="relative h-[80%] w-full flex justify-center items-center
                            [&>svg]:w-full [&>svg]:h-full
                            rounded-t-lg overflow-hidden bg-base-200 svg-checker p-2">
                {modelo.svg_2d && parse(
                    '<?xml version="1.0" encoding="UTF-8" standalone="no"?>' +
                    removeWidthAndHeight(modelo.svg_2d)
                )}
                {modelo.path_thumbnail_3d && (
                    <img
                        src={modelo.path_thumbnail_3d}
                        alt={`Render 3D de ${modelo.name}`}
                        className="absolute inset-0 h-full w-full object-contain bg-base-200 p-2
                                   opacity-0 group-hover:opacity-100 group-focus-within:opacity-100
                                   transition-opacity duration-200"
                    />
                )}
            </div>

            {/* Oscurece la previsualización para dar contraste a las acciones. */}
            <div className="absolute top-0 left-0 right-0 h-[80%] pointer-events-none
                            bg-black/0 group-hover:bg-black/30
                            opacity-0 group-hover:opacity-100
                            transition-all duration-200 z-10 rounded-t-lg" />

            {/* Title bar */}
            {showInfo && (
                <div className="h-[20%] border-t border-base-300 bg-base-300
                                flex flex-col items-center justify-center px-3 rounded-b-lg">
                    <h3 className="text-sm font-medium text-base-content w-full text-center
                                   truncate leading-tight"
                        title={capitalize(modelo?.name)}>
                        {capitalize(modelo?.name)}
                    </h3>
                    {/* La fecha del criterio activo: sin ella, ordenar por
                        edición o por creación da dos listas distintas sin nada
                        en pantalla que explique por qué. */}
                    {dateCaption(modelo, sortBy) && (
                        <span className="text-[11px] leading-tight text-base-content/60 truncate w-full text-center">
                            {dateCaption(modelo, sortBy)}
                        </span>
                    )}
                </div>
            )}
        </div>
    );
}

export default Modelo3dCard;
